import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  Employee,
  employeeDownloadMapper,
  EmployeeFilterBuilder,
  employeeGroupingOperator,
  updateEmployeeFilter
} from '@core/models/employee';
import { ActivatedRoute, Router } from '@angular/router';
import { FacetGroup } from '@core/models/facet';
import { firstValueFrom, forkJoin, from, Observable, Subscription } from 'rxjs';
import { AbstractGroupedList } from '@hoc/hoc-grouped-list';
import { AppliedFilter, FilterInstance, filterInstance, FILTERS } from '@core/prototypes/filtering';
import { facetsAsMenuItems, textAsMenuItem, typeaheadsAsMenuItems } from '@core/utils/utils';
import { InjectorKeys } from '@core/utils/injector.keys';
import { ApplicationConfiguration } from '@core/services/configuration/application.configuration.interface';
import { BackendService, TypeaheadItem } from '@core/prototypes/backend.service';
import { FacetsService } from '@core/services/facets/facet.service.interface';
import { addParamForRoute, GalaxyDrawerBlockingService, ignorePromise, MenuItem } from '@ebcont/ebcont.galaxy.js';
import { Customer } from '@core/models/customer';
import { Project } from '@core/models/project';
import { EmployeeListResult } from '@core/prototypes/list.result';
import { map } from 'rxjs/operators';
import { ListItem } from '@core/models/list-item.model';

@Component({
  selector: 'app-team-list',
  templateUrl: './team-list.component.html',
  styleUrls: ['./team-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TeamListComponent extends AbstractGroupedList<Employee> implements OnInit, OnDestroy {
  @Input() isExtended = true;
  @ViewChild('footerContainer') footerBlock: TemplateRef<Element> | undefined;

  searchValue: MenuItem = { id: '', label: '', isRouterLink: false } as MenuItem;
  autocompleteData: MenuItem[] = [];
  employeeEmail?: string;

  override lastResult: EmployeeListResult = {} as EmployeeListResult;

  protected filtersForBlock: FilterInstance[] = [];

  protected initialized = false;

  @Input() maxLabelsInFilters = 2;

  private drawerCloseSubscription: Subscription | undefined;

  protected applyNewFilter: EventEmitter<AppliedFilter> = new EventEmitter<AppliedFilter>();

  private initialUrlProcessed = false;

  constructor(
    @Inject(InjectorKeys.EMPLOYEES_SERVICE) private service: BackendService<Employee>,
    cd: ChangeDetectorRef,
    router: Router,
    route: ActivatedRoute,
    @Inject(InjectorKeys.CONFIGURATION) public config: ApplicationConfiguration,
    @Inject(InjectorKeys.FACETS_SERVICE) private facetsService: FacetsService,
    @Inject(InjectorKeys.CUSTOMERS_SERVICE) private customersService: BackendService<Customer>,
    @Inject(InjectorKeys.PROJECTS_SERVICE) private projectsService: BackendService<Project>,
    private drawerBlockingService: GalaxyDrawerBlockingService
  ) {
    super(service, employeeGroupingOperator, config, cd, router, route);
    this.loadMode = 'paged'; // now infinite loading mode is used
    this.lastResult.totalCountries = 0;
  }

  ngOnInit(): void {
    this.filter = new EmployeeFilterBuilder(this.config).build();
    const facetsObs = from(firstValueFrom(this.facetsService.getFacetGroup()));
    const customerObs = from(firstValueFrom(this.customersService.typeahead('')));
    const projectsObs = from(this.projectsService.typeahead(''));
    this.route.queryParams.subscribe((queryParams) => {
      this.employeeEmail = queryParams['employee'];
      const pageNumber = parseInt(queryParams['pageNumber'] || 0);
      const pageSize = parseInt(queryParams['pageSize'] || this.config.pageSize);

      this.filter.pageNumber = pageNumber;
      this.filter.pageSize = pageSize;
      this.pagination.currentPage = pageNumber;
      this.pagination.pageSize = pageSize;

      this.cd.detectChanges();
    });

    this.drawerCloseSubscription = this.drawerBlockingService.closeNotification$.subscribe(() => {
      this.closeEmployeeDetails();
    });

    forkJoin({
      facets: facetsObs,
      customers: customerObs,
      projects: projectsObs
    }).subscribe({
      next: ({ facets, customers, projects }) => {
        this.registerFacetFilters(facets);
        this.registerCustomersFilter(customers);
        this.registerProjectsFilter(projects);
      },
      complete: () => {
        this.initialized = true;
        setTimeout(() => {
          this.initialUrlProcessed = true;
          this.cd.markForCheck();
        }, 500);
        this.cd.markForCheck();
        this.cd.detectChanges();
      }
    });
  }

  ngOnDestroy(): void {
    this.drawerCloseSubscription?.unsubscribe();
  }

  public handleEmptyList(): void {
    this.searchValue = textAsMenuItem('');
    this.applyFilter();
  }

  /**
   * Reloads the current page with the current filter values in the query parameter.
   */
  private applyFilter() {
    const newFilter = {
      ...this.filter };

    Reflect.deleteProperty(newFilter, 'isAdmin');

    if (this.initialUrlProcessed) {
      Reflect.deleteProperty(newFilter, 'employee');
    }

    this.filter = newFilter;
    this.sorting = Reflect.get(this.filter, 'sorting');
    this.router.navigate(['team'], { queryParams: newFilter });
    this.reload();
  }

  /**
   * Register all the filters depending on the facets.
   * @param facets The facets returned from the backend.
   * @private
   */
  private registerFacetFilters(facets: FacetGroup) {
    this.filtersForBlock = [
      filterInstance(FILTERS.LOCATIONS, facetsAsMenuItems(facets.locations), {
        switchMode: true,
        maxLabels: this.maxLabelsInFilters,
        isMulti: true
      }),
      filterInstance(FILTERS.COMPANIES, facetsAsMenuItems(facets.companies), {
        switchMode: true,
        isMulti: true,
        maxLabels: this.maxLabelsInFilters
      }),
      filterInstance(FILTERS.AREAS, facetsAsMenuItems(facets.areas), {
        switchMode: true,
        isMulti: true,
        maxLabels: this.maxLabelsInFilters
      }),
      filterInstance(FILTERS.GREMIEN, facetsAsMenuItems(facets.gremien), {
        switchMode: true,
        isMulti: true,
        maxLabels: this.maxLabelsInFilters
      }),
      filterInstance(FILTERS.LANGUAGES, facetsAsMenuItems(facets.languages), {
        switchMode: true,
        isMulti: true,
        maxLabels: this.maxLabelsInFilters
      })
    ];
  }

  closeEmployeeDetails(): void {
    this.employeeEmail = undefined;
    ignorePromise(addParamForRoute(this.router, this.route, { employee: null, tab: null }));
  }

  private registerCustomersFilter(customers: TypeaheadItem[]) {
    this.filtersForBlock.push(
      filterInstance(FILTERS.CUSTOMERS, typeaheadsAsMenuItems(customers), {
        switchMode: true,
        maxLabels: this.maxLabelsInFilters,
        isMulti: true,
        searchable: true
      })
    );
  }

  private registerProjectsFilter(projects: TypeaheadItem[]) {
    this.filtersForBlock.push(
      filterInstance(FILTERS.PROJECTS, typeaheadsAsMenuItems(projects), {
        switchMode: true,
        maxLabels: this.maxLabelsInFilters,
        isMulti: true,
        searchable: true
      })
    );
  }

  /**
   * Searches for a name to serve the autocomplete dropdown.
   * @param text
   */
  protected searchForName = (text: string): Observable<MenuItem[]> => {
    return this.service.autocomplete(text).pipe(
      map((suggest: string[]) => {
        return suggest.map((item) => {
          return textAsMenuItem(item);
        });
      })
    );
  };

  filterBlockChanged(value: Record<string, string>) {
    if (!this.initialized) {
      return;
    }

    const oldFilter = this.filterToString(); // the current filter
    this.filter = updateEmployeeFilter(this.filter, value);
    const newFilter = this.filterToString(); // the new filter
    // the update is also triggered after a reload, and when there is no real change, then we should not
    // apply the filter, which would cause a reload of the same data and inducing an infinite loop
    if (oldFilter !== newFilter) {
      this.filter.pageNumber = 0;
      this.applyFilter();
    }
  }

  private filterToString() {
    return JSON.stringify({ ...this.filter, tab: undefined, employee: undefined });
  }

  /**
   * When the user selects an employee, we need to show the employee details in the drawer.
   * @ignore
   * @param item
   */
  public employeeSelected(item: Employee) {
    ignorePromise(
      addParamForRoute(this.router, this.route, { employee: item.email }).then(() => {
        this.employeeEmail = item.email;
      })
    );
    this.cd.detectChanges();
  }

  companySelected(company: string) {
    this.applyNewFilter.emit({ filter: FILTERS.COMPANIES, value: company });
  }

  locationSelected(location: string) {
    this.applyNewFilter.emit({ filter: FILTERS.LOCATIONS, value: location });
  }

  boardSelected(board: string) {
    this.applyNewFilter.emit({ filter: FILTERS.GREMIEN, value: board });
  }

  public downloadTheList() {
    ignorePromise(
      this.backendService.downloadList(this.filter, employeeDownloadMapper, 'employees', this.pagination.totalItems)
    );
  }

  public getTeamListItem(employeeItem: Employee) {
    let data: ListItem = {
      alignSm: 'row',
      columns: [
        {
          image: {
            src: employeeItem.email ?? ''
          }
        },
        {
          lines: [
            {
              content: employeeItem.businessPartnerFormattedName,
              strong: true
            },
            {
              content: employeeItem.roleDescription ?? 'n/a'
            }
          ]
        },
        {
          hiddenSm: true,
          lines: [
            {
              content: employeeItem.companyName ?? '',
              strong: true
            },
            {
              content: employeeItem.email ?? ''
            }
          ]
        },
        {
          hiddenSm: true,
          lines: [
            {
              content: employeeItem.location ?? '',
              strong: true
            },
            {
              content: employeeItem.phone ?? ''
            }
          ]
        }
      ]
    };

    return data;
  }
}
